import { Modal, Button, Heading } from '@sendible/design-system/src';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { subscribe } from '@sendible/event-manager';
import EVENTS from '@sendible/event-manager/src/event-types';
import { loadCanvaScript, createCanvaPlaceholder, triggerCanva } from './canvaScriptLoader';
import { CanvaButtonsContainer, FlexContentCanva, Container } from '../styles/index.styles';
import { LoadingState } from '../LoadingState';
import { useComposeBoxContext } from '../../../composeBoxContext';

interface CanvaCallbackData {
  exportUrl: string;
}

export const CanvaModal = () => {
  const { setIsCanvaModalOpen, uploadUrlsToCompose } = useComposeBoxContext();
  const { t } = useTranslation(['common_terms']);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeCanva = async () => {
      try {
        createCanvaPlaceholder();
        await loadCanvaScript();
        setIsLoading(false);
      } catch {
        setError(t('failed_to_load_canva'));
        setIsLoading(false);
      }
    };

    initializeCanva();

    const unsubscribe = subscribe(EVENTS.CANVA_IMAGE_CALLBACK, (data: CanvaCallbackData) => {
      if (data?.exportUrl) {
        uploadUrlsToCompose([{ url: data.exportUrl, name: 'Canva Design' }]);
        setIsCanvaModalOpen(false);
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleCanvaButtonClick = (canvaType: string) => {
    triggerCanva(canvaType);
  };

  return (
    <Modal
      close={() => setIsCanvaModalOpen(false)}
      headingIcon="canva"
      headingTitle={t('canva', { ns: 'common_terms' }) as string}
      variation="large"
    >
      <Container>
        <FlexContentCanva>
          {isLoading && <LoadingState useSpinner />}
          {error && <div>{error}</div>}
          {!isLoading && !error && (
            <CanvaButtonsContainer>
              <Heading
                align="center"
                color="brandDarkTint20"
                semantic="h1"
                variation="list-title_12"
              >
                {t('start_creating')}
              </Heading>
              <Button
                appearance="default"
                icon="image"
                label={t('image') as string}
                onClick={() => handleCanvaButtonClick('FacebookPost')}
                data-canva-type="FacebookPost"
                className="canva-trigger-facebookPost canva-trigger-button"
                size={14}
                type="button"
                variation="outline"
              />
              <Button
                appearance="default"
                icon="facebook_color"
                label={t('design_a_facebook_post') as string}
                onClick={() => handleCanvaButtonClick('FacebookPost')}
                data-canva-type="FacebookPost"
                className="canva-trigger-facebookPost canva-trigger-button"
                size={14}
                type="button"
                variation="outline"
              />
              {window.js_featureTagsManager?.isFeatureTag('enablePinterest') && (
                <Button
                  appearance="default"
                  icon="pinterest_color"
                  label={t('design_a_pinterest_pin') as string}
                  onClick={() => handleCanvaButtonClick('PinterestGraphic')}
                  data-canva-type="PinterestGraphic"
                  className="canva-trigger-pinterest canva-trigger-button"
                  size={14}
                  type="button"
                  variation="outline"
                />
              )}
              <Button
                appearance="default"
                icon="x_color"
                label={t('design_a_twitter_post') as string}
                onClick={() => handleCanvaButtonClick('TwitterPost')}
                data-canva-type="TwitterPost"
                className="canva-trigger-twitterPost canva-trigger-button"
                size={14}
                type="button"
                variation="outline"
              />
              <Button
                appearance="default"
                icon="instagram_color"
                label={t('design_an_instagram_post') as string}
                onClick={() => handleCanvaButtonClick('InstagramPost')}
                data-canva-type="InstagramPost"
                className="canva-trigger-instagramPost canva-trigger-button"
                size={14}
                type="button"
                variation="outline"
              />
            </CanvaButtonsContainer>
          )}
        </FlexContentCanva>
      </Container>
    </Modal>
  );
};
